import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { PackageSearch, CheckCircle2, Circle, Loader2, FileDown, Truck, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PaymentOptions, { PaymentBadge } from "@/components/PaymentOptions";
import { useCart } from "@/lib/cart";
import { useProducts } from "@/lib/products";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const { add, setDrawerOpen } = useCart();
  const products = useProducts();

  const reorder = () => {
    let added = 0;
    order.items.forEach((l) => {
      const p = products.find((x) => x.id === l.product_id);
      if (p) { add(p, l.qty); added += 1; }
    });
    if (added) { toast.success(`${added} item${added > 1 ? "s" : ""} added to a new order request`); setDrawerOpen(true); }
    else toast.error("These items are no longer in the catalog");
  };

  const fetchOrder = async (id, mail) => {
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/orders/track`, { params: { order_id: id, email: mail } });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Lookup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("order_id") && params.get("email")) fetchOrder(params.get("order_id"), params.get("email"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = (e) => { e.preventDefault(); fetchOrder(orderId, email); };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="track-order-page">
      <div className="flex items-center gap-3 mb-2">
        <PackageSearch className="h-6 w-6 text-sky-700" />
        <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700">Laboratory Dispatch Lookup</p>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Track Your Order</h1>
      <p className="mt-3 text-base text-slate-600">
        No account needed — enter the Order Reference from your confirmation and the email used at signing.
      </p>

      <form onSubmit={lookup} className="mt-8 bg-white border border-slate-200 p-6 sm:p-8 grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="track-id">Order Reference Number</Label>
          <Input id="track-id" placeholder="ARS-00000-RUO" required value={orderId} onChange={(e) => setOrderId(e.target.value)}
            data-testid="track-order-input-id" className="rounded-sm mt-1.5 font-tech" />
        </div>
        <div>
          <Label htmlFor="track-email">Purchaser Email</Label>
          <Input id="track-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="track-order-input-email" className="rounded-sm mt-1.5" />
        </div>
        <Button type="submit" disabled={loading} data-testid="track-order-submit-btn" className="sm:col-span-2 gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Locating..." : "Locate Order"}
        </Button>
      </form>

      {error && (
        <div className="mt-6 border border-red-200 bg-red-50 text-red-700 text-sm p-4" data-testid="track-order-error">{error}</div>
      )}

      {order && (
        <div className="mt-8 bg-white border border-slate-200" data-testid="track-order-status-result">
          <div className="p-6 border-b border-slate-200 flex flex-wrap justify-between gap-3">
            <div>
              <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500">Order</p>
              <p className="font-tech text-xl font-semibold text-slate-900">{order.order_id}</p>
              <p className="text-xs text-slate-500 mt-1">{order.customer_name}{order.entity ? ` · ${order.entity}` : ""}</p>
            </div>
            <div className="text-right">
              <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500">Total</p>
              <p className="font-tech text-xl font-semibold text-slate-900">${order.total.toFixed(2)}</p>
              <p className="text-xs text-slate-500 mt-1">Placed {new Date(order.created_at).toLocaleDateString()}</p>
              <div className="mt-2"><PaymentBadge payment={order.payment} /></div>
            </div>
          </div>
          <div className="p-6 border-b border-slate-200" data-testid="track-order-payment-section">
            <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-4">Payment</p>
            <PaymentOptions orderId={order.order_id} email={email} payment={order.payment} total={order.total}
              onUpdate={(p) => setOrder({ ...order, payment: p })} />
          </div>
          {order.shipment && (
            <div className="p-6 border-b border-slate-200 bg-sky-50/60" data-testid="track-order-shipment-section">
              <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-sky-800 mb-3 flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Shipment</p>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-sm text-slate-700">
                  <p><span className="text-slate-500">Carrier</span> · <strong>{order.shipment.carrier}</strong></p>
                  <p className="font-tech mt-0.5" data-testid="track-order-tracking-number">{order.shipment.tracking_number}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Dispatched {new Date(order.shipment.dispatched_at).toLocaleDateString()}{order.shipment.note ? ` · ${order.shipment.note}` : ""}</p>
                </div>
                {order.shipment.tracking_url && (
                  <a href={order.shipment.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-order-carrier-link"
                    className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap">
                    Track with {order.shipment.carrier} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </div>
          )}
          <div className="p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="track-order-agreement-section">
            <div>
              <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500">Signed Research Purchaser Agreement</p>
              <p className="text-sm text-slate-600 mt-1">Timestamped record of the agreement and attestations you signed for this order.</p>
            </div>
            <a href={`${API}/orders/agreement-pdf?order_id=${order.order_id}&email=${encodeURIComponent(email)}`} target="_blank" rel="noopener noreferrer"
              data-testid="track-order-agreement-pdf" className="inline-flex items-center justify-center gap-2 border border-slate-300 bg-white hover:border-slate-900 text-sm font-medium text-slate-800 px-5 py-2.5 rounded-full transition-colors whitespace-nowrap">
              <FileDown className="h-4 w-4" /> Download PDF
            </a>
          </div>
          <div className="p-6 border-b border-slate-200">
            <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-4">Dispatch Timeline</p>
            <div className="space-y-4">
              {order.timeline.map((t) => (
                <div key={t.label} className="flex items-center gap-3">
                  {t.done
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                  <p className={`text-sm ${t.done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{t.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500">Compounds</p>
              <Button size="sm" variant="outline" onClick={reorder} disabled={!products.length} data-testid="track-order-reorder-btn" className="gap-1.5">
                <RotateCcw className="h-3.5 w-3.5" /> Reorder these items
              </Button>
            </div>
            {order.items.map((l) => (
              <div key={l.product_id} className="flex justify-between items-start gap-3 text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`track-item-${l.product_id}`}>
                <div>
                  <span className="text-slate-700">{l.name} <span className="font-tech text-xs text-slate-400">× {l.qty}</span></span>
                  {l.has_coa_file && (
                    <a href={`${API}/coa/${l.product_id}`} target="_blank" rel="noopener noreferrer" data-testid={`track-item-coa-link-${l.product_id}`}
                      className="mt-0.5 flex items-center gap-1 font-tech text-[11px] text-emerald-700 hover:text-emerald-900 transition-colors">
                      <FileDown className="h-3 w-3" /> Download COA{l.coa_lot ? ` · Lot ${l.coa_lot}` : ""}
                    </a>
                  )}
                </div>
                <span className="font-tech text-slate-900">${l.line_total.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
